import React from 'react';
import { motion } from 'framer-motion';
import Planet from './Planet';

interface SkillCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  technologies: string[];
  position: { x: number; y: number; z: number };
  planetMode: {
    radius: number;
    orbitRadius: number;
    rotationSpeed: number;
    texture: string;
    atmosphere: {
      color: string;
      intensity: number;
    };
    moons: Array<{
      name: string;
      orbitRadius: number;
      size: number;
      speed: number;
    }>;
  };
  color: string;
  level: number;
  influences: string[];
  dependencies: string[];
  connections: Array<{
    targetId: string;
    strength: number;
    type: 'influence' | 'dependency' | 'synergy' | 'orbit';
  }>;
}

interface PlanetZoomViewProps {
  focusedSkill: SkillCategory;
  surroundingPlanets: SkillCategory[];
  onZoomOut: () => void;
  onSurroundingPlanetClick: (planetId: string) => void;
  onPlanetPosition: (skill: SkillCategory, index: number, total: number) => { x: number; y: number; z: number };
}

const PlanetZoomView: React.FC<PlanetZoomViewProps> = ({
  focusedSkill,
  surroundingPlanets,
  onZoomOut,
  onSurroundingPlanetClick,
  onPlanetPosition
}) => {
  return (
    <motion.div
      className="planet-zoom-overlay planet-zoom-overlay--active depth-layer-modals"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Zoom Exit Button */}
      <button
        className="zoom-exit-button depth-layer-tooltips"
        onClick={onZoomOut}
        aria-label="Exit planet zoom view"
      >
        ✕
      </button>

      {/* Focused Planet Container */}
      <motion.div
        className="focused-planet-container depth-layer-focused-planet"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate3d(-50%, -50%, 0)',
          zIndex: 100
        }}
      >
        <Planet
          skill={focusedSkill}
          className={`planet--size-xlarge planet--${focusedSkill.id.replace('_', '-')} planet--focused planet--solo-focus`}
          onClick={() => {}} // No action needed for focused planet
          isFocused={true}
          onMoonHover={() => {}} // Moons handle their own hover states
          onMoonClick={() => {}} // Handle moon clicks if needed
        />
      </motion.div>

      {/* Planet Information Panel */}
      <motion.div
        className="planet-info-panel depth-layer-panels"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translate3d(-50%, 0, 0)',
          background: 'rgba(0, 0, 0, 0.9)',
          border: `2px solid ${focusedSkill.color}`,
          borderRadius: '1rem',
          padding: '1.5rem 2rem',
          maxWidth: '500px',
          backdropFilter: 'blur(10px)',
          boxShadow: `0 0 30px ${focusedSkill.color}`,
          textAlign: 'center'
        }}
      >
        <h3 
          style={{ 
            color: focusedSkill.color,
            fontFamily: 'var(--font-secondary)',
            fontSize: '1.5rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '1rem',
            textShadow: `0 0 10px ${focusedSkill.color}`
          }}
        >
          {focusedSkill.icon} {focusedSkill.name}
        </h3>
        
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1rem',
          lineHeight: '1.6',
          marginBottom: '1rem'
        }}>
          {focusedSkill.description}
        </p>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          alignItems: 'center'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              color: 'var(--text-primary)',
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              marginBottom: '0.25rem'
            }}>
              Proficiency
            </div>
            <div style={{ 
              color: focusedSkill.color,
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}>
              {focusedSkill.level}%
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              color: 'var(--text-primary)',
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              marginBottom: '0.25rem'
            }}>
              Technologies
            </div>
            <div style={{ 
              color: focusedSkill.color,
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}>
              {focusedSkill.planetMode.moons.length}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              color: 'var(--text-primary)',
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              marginBottom: '0.25rem'
            }}>
              Experience
            </div>
            <div style={{ 
              color: focusedSkill.color,
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}>
              Expert
            </div>
          </div>
        </div>
      </motion.div>

      {/* Instructions */}
      <motion.div
        className="zoom-instructions depth-layer-ui"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        style={{
          position: 'absolute',
          top: '2rem',
          left: '2rem',
          color: 'var(--text-secondary)',
          fontSize: '0.9rem',
          fontFamily: 'var(--font-terminal)',
          maxWidth: '300px'
        }}
      >
        <div style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
          🌙 Explore technology moons floating around the planet
        </div>
        <div style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
          💫 Each moon represents a core technology or skill
        </div>
        <div style={{ color: 'var(--text-primary)' }}>
          ✕ Click the exit button to return to solar system view
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PlanetZoomView; 